import { AuthorizedBaseAxios, BaseAxios } from 'core/axios/axios';
import { environment } from 'config/environment';
import { all, call, takeEvery } from 'redux-saga/effects';
import { authActions } from 'state/auth/auth.actions';
import { getType } from 'typesafe-actions';
import { OAuthResponse } from '@summer/react-kit';
import { putEmptyAction, putPayloadAction } from 'common/reduxSaga';
import JwtDecode from 'jwt-decode';
import { TokenPayload, UserRole } from 'models/token';
import { message } from 'antd';

function logout() {
  return AuthorizedBaseAxios.delete('api/tokens');
}

function* processLogout() {
  try {
    yield call(logout);
    yield putEmptyAction(authActions.logoutSuccess)();
  } catch (error) {
    yield putPayloadAction(authActions.logoutError)(error);
  }
}
function* logoutWatcher() {
  yield takeEvery(getType(authActions.logout), processLogout);
}

function login(username: string, password: string): Promise<string> {
  const form = new FormData();
  form.set('grant_type', 'password');
  form.set('username', username);
  form.set('password', password);

  return new Promise((resolve, reject) => {
    BaseAxios.post<OAuthResponse>('oauth/token', form, {
      headers: {
        Authorization: `Basic ${environment.baseAuth}`,
        'content-type': 'multipart/form-data ',
      },
    })
      .then((response) => {
        localStorage.setItem('refresh_token', response.data.refresh_token);
        const role = (JwtDecode(response.data.access_token) as TokenPayload)
          .role;

        if (
          [
            UserRole.Admin,
            UserRole.SuperAdmin,
            UserRole.MedicalOperator,
            UserRole.DemoAccount,
          ].includes(role)
        ) {
          return resolve(response.data.access_token);
        } else {
          message.error('Access denied: invalid role');
          processLogout();
          throw Error;
        }
      })
      .catch(reject);
  });
}

function mfaLogin(
  mfaVerificationCode: string,
  username: string
): Promise<string> {
  return new Promise((resolve, reject) => {
    AuthorizedBaseAxios.post<OAuthResponse>('api/tokens/mfa', {
      mfaVerificationCode,
      username,
    })
      .then((response) => resolve(response.data.access_token))
      .catch(reject);
  });
}

function* processLogin(action: ReturnType<typeof authActions.login>) {
  const { username, password } = action.payload;
  try {
    const token = yield call(login, username, password);
    yield putPayloadAction(authActions.loginSuccess)({ token, username });
  } catch (error) {
    yield putPayloadAction(authActions.loginError)(error);
  }
}

function* processMfaLogin(action: ReturnType<typeof authActions.mfaLogin>) {
  const { username, mfaVerificationCode } = action.payload;
  try {
    const token = yield call(mfaLogin, mfaVerificationCode, username);
    yield putPayloadAction(authActions.mfaLoginSuccess)({ token, username });
  } catch (error) {
    yield putPayloadAction(authActions.mfaLoginError)(error);
  }
}

function* loginWatcher() {
  yield takeEvery(getType(authActions.login), processLogin);
}

function* mfaLoginWatcher() {
  yield takeEvery(getType(authActions.mfaLogin), processMfaLogin);
}

function loginWithToken(token: string) {
  return new Promise((resolve, reject) => {
    BaseAxios.post<OAuthResponse>(
      `api/users/login/token`,
      { token },
      {
        headers: {
          Authorization: `Basic ${environment.baseAuth}`,
        },
      }
    )
      .then((response) => resolve(response.data.access_token))
      .catch(reject);
  });
}

function* processTokenLogin(action: ReturnType<typeof authActions.tokenLogin>) {
  try {
    const token = yield call(loginWithToken, action.payload);
    yield putPayloadAction(authActions.loginSuccess)(token);
  } catch (error) {
    yield putPayloadAction(authActions.loginError)(error);
  }
}

function* tokenLoginWatcher() {
  yield takeEvery(getType(authActions.tokenLogin), processTokenLogin);
}

export function* authSagas() {
  yield all([
    loginWatcher(),
    mfaLoginWatcher(),
    tokenLoginWatcher(),
    logoutWatcher(),
  ]);
}
